// client/src/components/MerchantAnalyseTransaction.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { FaArrowLeft, FaGift, FaShoppingCart } from 'react-icons/fa';
import MerchantNavbar from './MerchantNavbar';
import { firestore } from '../../firebase';

const formatCompactDateFR = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
  const WD = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const MO = ['jan', 'fev', 'mar', 'avr', 'mai', 'jun', 'jul', 'aou', 'sep', 'oct', 'nov', 'dec'];
  const wd = WD[dateObj.getDay()];
  const d = dateObj.getDate();
  const m = MO[dateObj.getMonth()];
  const y = dateObj.getFullYear();
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const mm = String(dateObj.getMinutes()).padStart(2, '0');
  return `${wd} ${d} ${m} ${y} ${hh}:${mm}`;
};

const parseFlexibleNumber = (val) => {
  if (val === null || val === undefined) return 0;
  const s = String(val).replace(',', '.').trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const formatPts = (n) => {
  const value = parseFlexibleNumber(n);
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const toDate = (value) => {
  if (!value) return new Date(0);
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  return new Date(value);
};

const MerchantAnalyseTransaction = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [merchantUid, setMerchantUid] = useState(null);
  const [settings, setSettings] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        setMerchantUid(null);
        setCustomerData(null);
        setLoading(false);
        return;
      }
      setMerchantUid(currentUser.uid);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!merchantUid) return;
    const fidelityRef = doc(firestore, 'fidelity', merchantUid);
    const unsubscribe = onSnapshot(
      fidelityRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const type = data.Type || 'Fidbycount';
        const multiple = parseInt(data.multiple ?? 5, 10) || 5;
        const pointsPaliers = [
          { points: data.points1, gift: data.gift1 },
          { points: data.points2, gift: data.gift2 },
          { points: data.points3, gift: data.gift3 },
        ].filter((p) => parseFlexibleNumber(p.points) > 0);
        setSettings({
          type,
          multiple,
          gift: data.gift || '',
          pointsPaliers: type === 'Fidbypoints' ? pointsPaliers : [],
        });
      },
      (error) => { console.error('Settings listener error', error); }
    );
    return () => unsubscribe();
  }, [merchantUid]);

  useEffect(() => {
    if (!merchantUid || !customerId) return;
    setLoading(true);
    const listDocRef = doc(firestore, 'fidelity', merchantUid, 'list', customerId);
    const unsubscribe = onSnapshot(
      listDocRef,
      (snap) => {
        setCustomerData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      },
      (error) => {
        console.error('Customer listener error', error);
        setCustomerData(null);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [merchantUid, customerId]);

  useEffect(() => {
    let cancelled = false;
    if (!customerId) return undefined;
    (async () => {
      try {
        const userSnap = await getDoc(doc(firestore, 'users', customerId));
        if (cancelled) return;
        setCustomerProfile(userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } : null);
      } catch (error) {
        console.error('Customer profile fetch error', error);
        if (!cancelled) setCustomerProfile(null);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const flattenedHistory = useMemo(() => {
    if (!customerData) return [];
    const timestamps = (customerData.timestamps || []).map((ts, index) => ({
      id: `ts-${index}`,
      date: toDate(ts.date),
      type: 'timestamp',
      count: ts.count ?? 1,
    }));

    const resetstamps = (customerData.resetstamp || []).map((rs, index) => ({
      id: `rs-${index}`,
      date: toDate(rs.date),
      type: 'resetstamp',
      count: rs.count ?? 1,
      giftAtTime: rs.giftAtTime,
    }));

    const pointshistory = (customerData.pointshistory || []).map((ph, index) => ({
      id: `ph-${index}`,
      date: toDate(ph.date),
      type: 'pointshistory',
      points: parseFlexibleNumber(ph.points),
      montant: parseFlexibleNumber(ph.montant),
      gift: ph.gift,
    }));

    return [...timestamps, ...resetstamps, ...pointshistory].sort((a, b) => b.date - a.date);
  }, [customerData]);

  

  const compteur = parseInt(customerData?.compteur, 10) || 0;
  const points = parseFlexibleNumber(customerData?.points);
  const multiple = settings?.multiple || 0;

  const visibleHistory = showAll ? flattenedHistory : flattenedHistory.slice(0, 12);

  return (
    <div>
      <MerchantNavbar />
      <div className="dashboard-container">
        <style>{`
          .mat-scope { width: 100%; min-width: 0; }
          .mat-scope .mat-back { margin-bottom: 12px; padding-left: 8px; }
          .mat-scope .mat-section-title { margin: 0 0 12px; font-size: 17px; font-weight: 650; color: var(--eb-text); text-align: left; }
          .mat-scope .mat-empty { margin: 0; padding: 32px 16px; text-align: center; color: var(--eb-muted); font-size: 14px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); }

          .mat-scope .table-fidelity-wrap { width: 100%; overflow-x: auto; }
          .mat-scope .table-fidelity { width: 100%; border-collapse: separate; border-spacing: 0; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); overflow: hidden; box-shadow: var(--eb-shadow-sm); font-size: 14px; }
          .mat-scope .table-fidelity thead th { background: var(--eb-surface-2); color: var(--eb-muted); font-weight: 600; font-size: 12.5px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid var(--eb-border); padding: 12px 16px; text-align: left; }
          .mat-scope .table-fidelity td { padding: 12px 16px; text-align: left; vertical-align: middle; word-break: break-word; border-bottom: 1px solid var(--eb-border); color: var(--eb-text-2); }
          .mat-scope .table-fidelity tbody tr:last-child td { border-bottom: 0; }
          .mat-scope .table-fidelity tbody tr:hover { background: var(--eb-surface-2); }
          .mat-scope .pill { display: inline-flex; align-items: center; gap: 4px; font-weight: 600; padding: 3px 10px; border-radius: var(--eb-radius-pill); border: 1px solid transparent; font-size: 13px; white-space: nowrap; }
          .mat-scope .pill-scan { background: var(--eb-brand-50); color: var(--eb-brand); border-color: var(--eb-brand-100); }
          .mat-scope .pill-gift { background: var(--eb-warning-bg); color: var(--eb-warning); border-color: #fde3b0; }
          .mat-scope .pill-pos  { background: var(--eb-success-bg); color: var(--eb-success); border-color: #c6f0da; }
          .mat-scope .pill-neg  { background: var(--eb-danger-bg); color: var(--eb-danger); border-color: #fbd0cb; }

          .mat-scope .summary-card { margin: 0 0 24px; }
          .mat-scope .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; }
          .mat-scope .summary-block { background: var(--eb-surface); border-radius: var(--eb-radius-lg); padding: 20px; border: 1px solid var(--eb-border); box-shadow: var(--eb-shadow-sm); display: flex; flex-direction: column; gap: 6px; min-width: 0; }
          .mat-scope .summary-block.is-reached { border-color: #fde3b0; background: var(--eb-warning-bg); }
          .mat-scope .summary-label { font-size: 13.5px; color: var(--eb-muted); font-weight: 500; }
          .mat-scope .summary-value { font-size: 28px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); }
          .mat-scope .summary-strong { font-weight: 600; color: var(--eb-text); overflow-wrap: anywhere; }
          .mat-scope .summary-hint { font-size: 13.5px; color: var(--eb-muted); }

          .mat-scope .mat-more { display: flex; justify-content: center; margin-top: 16px; }

          .mat-scope .client-profile-header { margin: 0 0 16px; padding: 20px; display: flex; align-items: center; gap: 16px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); }
          .mat-scope .client-avatar { width: 56px; height: 56px; border-radius: 50%; object-fit: cover; flex-shrink: 0; border: 1px solid var(--eb-border); background: var(--eb-brand-50); }
          .mat-scope .client-avatar--placeholder { display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 700; color: var(--eb-brand); text-transform: uppercase; }
          .mat-scope .client-identity { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
          .mat-scope .client-name { font-size: 20px; font-weight: 700; letter-spacing: -0.01em; color: var(--eb-text); word-break: break-word; }
          .mat-scope .client-email { font-size: 14px; color: var(--eb-muted); word-break: break-all; }

          @media (max-width: 768px) {
            .mat-scope .client-profile-header, .mat-scope .summary-block { padding: 16px; }
            .mat-scope .summary-grid { gap: 12px; }
            .mat-scope .summary-value { font-size: 22px; }
            .mat-scope .table-fidelity { table-layout: fixed; font-size: 13px; }
            .mat-scope .table-fidelity thead th, .mat-scope .table-fidelity td { padding: 10px 8px; }
            .mat-scope .table-fidelity thead th { font-size: 11px; }
            .mat-scope .pill { padding: 3px 8px; font-size: 12.5px; }
          }
        `}</style>

        <div className="mat-scope">
          <button
            onClick={() => navigate('/merchantgestion')}
            className="eb-btn eb-btn--ghost eb-btn--sm mat-back"
          >
            <FaArrowLeft /> Retour
          </button>

        {customerProfile && (customerProfile.pseudo || customerProfile.email || customerProfile.photoURL) && (
          <div className="client-profile-header">
            {customerProfile.photoURL ? (
              <img src={customerProfile.photoURL} alt={customerProfile.pseudo || 'Client'} className="client-avatar" />
            ) : (
              <div className="client-avatar client-avatar--placeholder">
                {(customerProfile.pseudo || customerProfile.email || '?').trim().charAt(0)}
              </div>
            )}
            <div className="client-identity">
              <span className="client-name">{customerProfile.pseudo || 'Client'}</span>
              {customerProfile.email && <span className="client-email">{customerProfile.email}</span>}
            </div>
          </div>
        )}

        {loading ? (
          <div className="mat-empty" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <span className="eb-spinner" aria-hidden="true" />
            Chargement des transactions…
          </div>
        ) : !customerData ? (
          <p className="mat-empty">Aucune transaction trouvée pour ce client.</p>
        ) : (
          <>
            <div className="summary-card">
              <div className="summary-grid">
                {settings?.type === 'Fidbypoints' ? (
                  <>
                    <div className="summary-block">
                      <span className="summary-label">Points cumulés</span>
                      <span className="summary-value">{formatPts(points)} pts</span>
                    </div>
                    {settings.pointsPaliers.map((palier, index) => {
                      const reached = points >= parseFlexibleNumber(palier.points);
                      return (
                        <div key={index} className={`summary-block${reached ? ' is-reached' : ''}`}>
                          <span className="summary-strong">
                            {reached ? '🎁 Utilisable' : '🎯 Objectif'} : {palier.gift}
                          </span>
                          <span className="summary-hint">{formatPts(palier.points)} pts requis</span>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div className="summary-block">
                      <span className="summary-label">Passages validés</span>
                      <span className="summary-value">
                        {compteur}{multiple ? ` / ${multiple}` : ''} passages
                      </span>
                    </div>
                    {settings?.gift && (
                      <div className="summary-block">
                        <span className="summary-label">Prochain gain</span>
                        <span className="summary-strong">{settings.gift}</span>
                        {multiple ? <span className="summary-hint">Après {multiple} validations</span> : null}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div>
              <h2 className="mat-section-title">Historique des transactions</h2>
              {flattenedHistory.length === 0 ? (
                <p className="mat-empty">Aucun historique disponible.</p>
              ) : (
                <>
                  <div className="table-fidelity-wrap">
                    <table className="table-fidelity">
                      <thead>
                        <tr>
                          <th>Valeur</th>
                          <th>Date</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleHistory.map((row) => {
                          const formattedDate = formatCompactDateFR(row.date);
                          let value = '';
                          let typeLabel = '';
                          let pillClass = 'pill';

                          if (row.type === 'timestamp') {
                            typeLabel = 'Scans';
                            const count = row.count || 1;
                            value = (
                              <>
                                <FaShoppingCart aria-hidden="true" />
                                +{count}
                              </>
                            );
                            pillClass += ' pill-scan';
                          } else if (row.type === 'resetstamp') {
                            typeLabel = 'Gain utilisé';
                            value = (
                              <>
                                <FaGift aria-hidden="true" />
                                {row.count || 1}
                              </>
                            );
                            pillClass += ' pill-gift';
                          } else {
                            const pts = row.points || 0;
                            const isPositive = pts > 0;
                            typeLabel = isPositive ? 'Points ajoutés' : 'Points dépensés';
                            value = (
                              <>
                                {isPositive ? <FaShoppingCart aria-hidden="true" /> : <FaGift aria-hidden="true" />}
                                {isPositive ? '+' : ''}{formatPts(pts)} pts
                              </>
                            );
                            pillClass += isPositive ? ' pill-pos' : ' pill-neg';
                          }

                          return (
                            <tr key={row.id}>
                              <td>
                                <span className={pillClass}>{value}</span>
                              </td>
                              <td style={{ color: 'var(--eb-muted)' }}>{formattedDate}</td>
                              <td>{typeLabel}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {flattenedHistory.length > visibleHistory.length && (
                    <div className="mat-more">
                      <button type="button" className="show-more eb-btn eb-btn--secondary" onClick={() => setShowAll((prev) => !prev)}>
                        {showAll ? 'Afficher moins' : 'Afficher plus'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default MerchantAnalyseTransaction;
